import { AdapterRegistry, LinkedService, ReferenceAdapter } from 'shell-models-lib';
import { Metamodel } from 'app/model/base/model';
import { CallTransformation } from 'app/model/dataflow/calltransformation';
import { TransformationAdapter } from 'app/model/adapters/dataflow/transformations/base-transformation.adapter';
import { FLOW, ITransformation } from 'app/model/adapters/dataflow/IDataflow';
import { Model } from 'app/model/backendmodel';
import { DataFlowSchemaAdapterUtils } from '../dataflow-schema-adapter-utils';
import { DataFlowExpressionUtils } from '../expression.util';
import { BooleanDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { RestCallSettings } from 'app/model/dataflow/dataset-settings/restCall-settings';
import { isTruthy } from 'app/model/dataflow/dataflow-expression-utilities';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(CallTransformation),
    asynchronous: true
})
export class CallTransformationAdapter extends TransformationAdapter<CallTransformation> {

    public async getDslScript(callTransformation: CallTransformation): Promise<string> {
        const props = [];
        if (callTransformation.selectTx) {
            const columns = DataFlowSchemaAdapterUtils.getColumnsDsl(callTransformation.selectTx);
            props.push(columns);
            if (columns) {
                props.push(
                    `skipDuplicateMapInputs: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        callTransformation.selectTx.skipDuplicateMapInputs,
                        BooleanDataFlowType.TYPE
                    )}`
                );
                props.push(
                    `skipDuplicateMapOutputs: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        callTransformation.selectTx.skipDuplicateMapOutputs,
                        BooleanDataFlowType.TYPE
                    )}`
                );
            }
        }

        if (callTransformation.source.datasetSettings) {
            let schema = '';
            if (callTransformation.source.datasetSettings instanceof RestCallSettings) {
                const restCallSettings = callTransformation.source.datasetSettings as RestCallSettings;
                // REST dataset format
                const columns = [];
                if (restCallSettings.includeHeader) {
                    callTransformation.source.datasetSettings.responseHeaderColName.expression =
                        callTransformation.headerColumn.name;
                    columns.push(callTransformation.headerColumn);
                }
                if (restCallSettings.includeBody) {
                    callTransformation.source.datasetSettings.responseBodyColName.expression =
                        callTransformation.staticBodyColumn.name;
                    if (callTransformation.staticBodyDataFlowExpression.expression !== '') {
                        columns.push(callTransformation.staticBodyColumn);
                    }
                }
                if (restCallSettings.includeStatus) {
                    callTransformation.source.datasetSettings.responseStatusColName.expression =
                        callTransformation.statusColumn.name;
                    columns.push(callTransformation.statusColumn);
                }
                schema = DataFlowSchemaAdapterUtils.getOutputDefinitionDslForColumns(columns);
            } else {
                // SQL dataset format
                let columnStrings = '';
                if (callTransformation.staticBodyDataFlowExpression.expression !== '') {
                    columnStrings = callTransformation.staticBodyDataFlowExpression.expression;
                }
                schema = columnStrings ? `output${columnStrings}` : '';
            }
            if (schema) {
                props.push(schema);
            }
        }

        props.push(
            `allowSchemaDrift: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                callTransformation.source.allowSchemaDrift,
                BooleanDataFlowType.TYPE
            )}`
        );
        if (callTransformation.source.allowSchemaDrift && isTruthy(callTransformation.source.inferDriftedColumnTypes)) {
            props.push(
                `inferDriftedColumnTypes: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    callTransformation.source.inferDriftedColumnTypes,
                    BooleanDataFlowType.TYPE
                )}`
            );
        }

        if (callTransformation.source.datasetSettings) {
            const settings = callTransformation.source.dataTypeSettings;
            if (!!settings.dateFormats && settings.dateFormats.length > 0) {
                props.push(`dateFormats: [${settings.dateFormats.map((v) => `'` + v + `'`).join(',')}]`);
            }
            if (!!settings.timestampFormats && settings.timestampFormats.length > 0) {
                props.push(
                    `timestampFormats: [${settings.timestampFormats
                        .map((v) => `'` + DataFlowExpressionUtils.escapeSingleQuoteInDslString(v) + `'`)
                        .join(',')}]`
                );
            }
            if (!!settings.preferredIntegralType && settings.preferredIntegralType !== 'none') {
                props.push(`preferredIntegralType: '${settings.preferredIntegralType}'`);
            }
            if (!!settings.preferredFractionalType && settings.preferredFractionalType !== 'none') {
                props.push(`preferredFractionalType: '${settings.preferredFractionalType}'`);
            }
            if (!!settings.booleanTrueFormat && !!settings.booleanFalseFormat) {
                props.push(`booleanFormat: ['${settings.booleanTrueFormat}', '${settings.booleanFalseFormat}']`);
            }
        }

        const dataSetSettingsProps = [];
        let dataSetSettings = '';
        if (callTransformation.source.datasetSettings && callTransformation.source.sourceLinkedService) {
            const adapter = this.context.dataflowDSLAdapterContext.adapterFor(
                Metamodel.modelOf(callTransformation.source.datasetSettings)
            );
            dataSetSettingsProps.push(...await adapter.toDSL(callTransformation.source.datasetSettings, true));
            dataSetSettings = dataSetSettingsProps.join(',\n\t');

            props.push(dataSetSettings);
        }

        if(callTransformation.bodyColumnTypeParameter != null && callTransformation.bodyColumnTypeParameter.length > 0) {
            props.push(`columnTypeMap: ['${callTransformation.staticBodyColumn.name}'->'${callTransformation.bodyColumnTypeParameter}']`);
        }

        if (callTransformation.previewRequest) {
            props.push(`previewRequest: true`);
        }

        const dsl = props.filter((prop) => !!prop).join(',\n\t');

        return (
            `${this._getInputStreams(callTransformation)} ${CallTransformation.MODEL.name}(` +
            dsl +
            `) ${FLOW} ${this._getOutputStreams(callTransformation)}`
        );
    }

    protected async _toModel(resource: ITransformation, model: CallTransformation): Promise<CallTransformation> {
        // This means like a reference (don't actually create the model)
        const linkedService = ReferenceAdapter.toProxyModel<LinkedService>(
            Metamodel.modelFor(LinkedService),
            resource.linkedService, this.context
        );
        model.source.sourceLinkedService = linkedService;
        return model;
    }

    protected async _toResource(transformation: CallTransformation): Promise<ITransformation> {
        const resource: ITransformation = {
            name: transformation.name,
            description: transformation.description
        };

        if (transformation.source.sourceLinkedService) {
            resource.linkedService = ReferenceAdapter.toResource(
                transformation.source.sourceLinkedService,
                this.context
            ) as Model.LinkedServiceReference;
        }

        if (!this._isCombinedScriptEnabled) {
            resource.script = await this.getDslScript(transformation);
        }

        return resource;
    }
}
